import useLocalStorage from 'hooks/useLocalStorageState'
import {
  hotelCampaignProviderImportStatuses,
  hotelCampaignRegistrationStatuses,
  subsidiaryStatusSortRank
} from 'main/hotelStatuses'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import subsidiariesApi from 'services/api/subsidiaries'
import userApi from 'services/api/user'

import { getIssueText } from './HotelCard/status'

// Add jsDoc
/**
 *
 * @typedef Hotel
 * @property {string} hotelName
 * @property {string} bookingEngine
 * @property {string} chainCode
 * @property {string} parentChainCode
 * @property {string} textMarketingUrlKey
 * @property {string} addedOn
 * @property {number} addedBy
 * @property {string} updatedOn
 * @property {string} serviceStartDate
 * @property {number} updatedBy
 * @property {string} id
 * @property {number} numberOfRooms
 * @property {string} pms
 * @property {boolean} isMissingSomeInformation
 * @property {number} displayId
 * @property {boolean} isDeleted
 * @property {number} status
 * @property {object[]} subsidiaryPmsUsers
 * @property {number} subsidiaryPmsUsers.id
 * @property {string} subsidiaryPmsUsers.subsidiaryId
 * @property {string} subsidiaryPmsUsers.email
 * @property {string} subsidiaryPmsUsers.firstName
 * @property {string} subsidiaryPmsUsers.lastName
 * @property {string} subsidiaryPmsUsers.birthDate
 * @property {string} subsidiaryPmsUsers.addedOn
 * @property {number} subsidiaryPmsUsers.addedBy
 * @property {string} merchantName
 * @property {boolean} inviteBackIsDisabled
 * @property {object[]} inviteBlockOn
 * @property {string} inviteBlockOn.addedBy
 * @property {string} inviteBlockOn.addedOn
 * @property {string} inviteBlockOn.reason
 * @property {boolean} pmsSyncIsConnected
 * @property {boolean} isServiceStarted
 * @property {boolean} isCampaignsEnabled
 * @property {boolean} isTcrActivated
 * @property {boolean} isPhoneEnabled
 * @property {object[]} tags
 * @property {number} tags.id
 * @property {string} tags.name
 * @property {string} tags.addedBy
 * @property {string} tags.addedOn
 * @property {string} processKind
 *
 * @typedef {('onboarding' | 'upAndRunning' | 'needsAttention' | 'churnedPaused' | 'deleted')} HotelType
 *
 * @typedef {('hotel' | 'group' | 'brand' | 'addedOn' | 'updatedOn' | 'status')} SortParam
 *
 * @typedef {('up' | 'down')} SortDirection
 *
 * @callback SetSortParam
 * @param {SortParam} sortParam
 *
 *
 * @param {HotelType} type
 */
export function useHotelsData(type) {
  /**
   * @type {[Hotel[], React.Dispatch<React.SetStateAction<Hotel[]>>]} state
   */
  const [hotels, setHotels] = React.useState([])

  const csmUserId = useSelector(state => state.login.csmUserId)
  const [csmUsers, setCsmUsers] = React.useState([])

  const [isLoading, setIsLoading] = React.useState(true)
  const [searchQuery, setSearchQuery] = useLocalStorage(
    `${type}_hotelsFilterSearchQuery`,
    ''
  )

  const [sortParam, setSortParam] = useLocalStorage(
    `${type}_hotelsFilterSortParam`,
    'updatedOn'
  )
  const [sortDirection, setSortDirection] = useLocalStorage(
    `${type}_hotelsFilterSortDirection`,
    'down'
  )

  const [selectedParentChain, setSelectedParentChain] = useLocalStorage(
    `${type}_hotelsFilterParentChain`,
    'all'
  )
  const [selectedChain, setSelectedChain] = useLocalStorage(
    `${type}_hotelsFilterChain`,
    'all'
  )
  const [selectedGroup, setSelectedGroup] = useLocalStorage(
    `${type}_hotelsFilterGroup`,
    'all'
  )
  const [selectedStatus, setSelectedStatus] = useLocalStorage(
    `${type}_hotelsFilterStatus`,
    [
      'PendingPhoneNumberSetup',
      'PendingCampaignBrandRegistration',
      'BasicAccount',
      'MediumAccount',
      'LargeAccount',
      'AboutToLose'
    ]
  )
  const [selectedCsmIds, setSelectedCsmIds] = useLocalStorage(
    `${type}_hotelsFilterCsmIds`,
    []
  )

  const [fetchIndex, setFetchIndex] = useState(0)

  function clearFilters() {
    setSelectedParentChain('all')
    setSelectedChain('all')
    setSelectedGroup('all')
    setSelectedStatus([])
    setSearchQuery('')
    setSelectedCsmIds([])
  }

  React.useEffect(() => {
    async function getData() {
      setIsLoading(true)
      setHotels([])

      try {
        const getHotelListRes = await subsidiariesApi.getHotelList({ type })
        setHotels(getHotelListRes)

        const getCsmRes = await userApi.getCustomerSuccessManagers()
        setCsmUsers(getCsmRes)
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, fetchIndex])

  React.useEffect(() => {
    if (csmUserId) {
      setSelectedCsmIds([`${csmUserId}`])
    }
  }, [csmUserId])

  React.useEffect(() => {
    if ((type === 'onboarding' || type === 'upAndRunning') && sortParam === 'issue') {
      setSortParam('status')
    }
  }, [type, sortParam, setSortParam])

  const sortedAndFilteredHotels = React.useMemo(() => {
    const filteredHotels = hotels
      .filter(h => (selectedChain === 'all' ? true : h.chainCode === selectedChain))
      .filter(h =>
        selectedParentChain === 'all' ? true : h.parentChainCode === selectedParentChain
      )
      .filter(h => (selectedGroup === 'all' ? true : h.merchantName === selectedGroup))
      .filter(h => {
        if (selectedStatus.length === 0) {
          return true
        } else if (hotelCampaignRegistrationStatuses.includes(h.status)) {
          return selectedStatus.includes('PendingCampaignBrandRegistration')
        } else if (hotelCampaignProviderImportStatuses.includes(h.status)) {
          return selectedStatus.includes('PendingCampaignProviderImport')
        } else {
          return selectedStatus.includes(h.status)
        }
      })
      .filter(h => {
        if (selectedCsmIds.length === 0) {
          return true
        }

        if (h.csmUser === null) {
          return false
        }

        return selectedCsmIds.includes(`${h.csmUser.id}`)
      })
      .filter(h => h.searchString?.toLowerCase().includes(searchQuery.toLowerCase()))

    const sorttedHotels = filteredHotels.sort((a, b) => {
      switch (sortParam) {
        case 'hotel':
          return a.displayId - b.displayId
        case 'group':
          return a.merchantName?.toUpperCase() > b.merchantName?.toUpperCase() ? 1 : -1
        case 'brand':
          return a.parentChainCode?.toUpperCase() > b.parentChainCode?.toUpperCase()
            ? 1
            : -1
        case 'csm':
          return a.csmUser?.fullName?.toUpperCase() > b.csmUser?.fullName?.toUpperCase()
            ? 1
            : -1
        case 'addedOn':
          return new Date(a.addedOn) - new Date(b.addedOn)
        case 'updatedOn':
          return new Date(a.updatedOn) - new Date(b.updatedOn)
        case 'serviceStartDate':
          return new Date(a.serviceStartDate) - new Date(b.serviceStartDate)
        case 'status': {
          const ra = subsidiaryStatusSortRank[a.status] ?? 9999
          const rb = subsidiaryStatusSortRank[b.status] ?? 9999
          return ra - rb
        }
        case 'issue':
          return getIssueText(a).localeCompare(getIssueText(b), undefined, {
            sensitivity: 'base'
          })
        default:
          return 0
      }
    })

    return sortDirection === 'down' ? sorttedHotels : sorttedHotels.reverse()
  }, [
    hotels,
    searchQuery,
    selectedChain,
    selectedCsmIds,
    selectedGroup,
    selectedParentChain,
    selectedStatus,
    sortDirection,
    sortParam
  ])

  return {
    refetchData: () => setFetchIndex(fetchIndex + 1),
    sortedAndFilteredHotels,
    hotels,
    csmUsers,
    isLoading,
    sort: {
      sortParam,
      setSortParam,
      sortDirection,
      setSortDirection
    },
    filter: {
      selectedParentChain,
      setSelectedParentChain,
      selectedChain,
      setSelectedChain,
      selectedGroup,
      setSelectedGroup,
      selectedStatus,
      setSelectedStatus,
      clearFilters,
      selectedCsmIds,
      setSelectedCsmIds
    },
    search: {
      searchQuery,
      setSearchQuery
    }
  }
}
